import { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocalizationProvider, useLocalization } from './context/LocalizationContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { AIAvatarSessionProvider } from './context/AIAvatarSessionContext';
import { useUserData } from './hooks/useUserData';
import { useSessionEndedListener } from './hooks/useSessionEndedListener';
import { usePodData } from './hooks/usePodData';
import Dashboard from './pages/Dashboard';
import Welcome from './pages/Welcome';
import Instructions from './pages/Instructions';
import VitalsCheck from './components/VitalsCheck';
import Consultation from './pages/Consultation';
import EndSession from './pages/EndSession';
import LoadingScreen from './components/LoadingScreen';
import Assessment from './pages/Assessment';
import AssessmentDetail from './pages/AssessmentDetail';
import Settings from './pages/Settings';
import RedirectPage from './pages/RedirectPage';
import { Toaster } from 'react-hot-toast';
import { TOAST_CONFIG } from './constants';
import './App.css';
import TopBar from './components/shared/TopBar';
import WebSocketStatus from './components/WebSocketStatus';
import { isPodIP, getUrlPrefix, savePodIP, getSavedPodIP, getUnprefixedPath } from './utils/helpers';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import { tokenStorage } from './utils/tokenStorage';

const PrefixedRoutes = ({ userData }) => {
  const { prefix } = useParams();
  const { loadPodData } = usePodData();

  useEffect(() => {
    if (isPodIP(prefix)) {
      savePodIP(prefix);
      loadPodData(prefix);
    }
  }, [prefix]);

  return (
    <Routes>
      {/* Public */}
      <Route index element={<Welcome userData={userData} />} />
      <Route path="welcome" element={<Welcome userData={userData} />} />
      <Route path="settings" element={<Settings />} />

      {/* private */}
      <Route path="dashboard" element={<ProtectedRoute><Dashboard userData={userData} /></ProtectedRoute>} />
      <Route path="instructions" element={<ProtectedRoute><Instructions /></ProtectedRoute>} />
      <Route path="vitals" element={<ProtectedRoute><VitalsCheck userData={userData} /></ProtectedRoute>} />
      <Route path="consultation/:podId?" element={<ProtectedRoute><Consultation userData={userData} /></ProtectedRoute>} />
      <Route path="assessment" element={<ProtectedRoute><Assessment userData={userData} /></ProtectedRoute>} />
      <Route path="assessment/:type" element={<ProtectedRoute><AssessmentDetail /></ProtectedRoute>} />
      <Route path="end" element={<ProtectedRoute><EndSession userData={userData} /></ProtectedRoute>} />


      <Route path="*" element={<LoadingScreen />} />
    </Routes>
  );
};

const AppContent = () => {
  const { userData, loading, error } = useUserData();
  const { isLoading: authLoading, error: authError } = useAuth();
  const { setUserData } = useAppContext();
  const { t } = useLocalization();
  const lastStoredUserData = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useSessionEndedListener();

  // Handle root redirect and prefix persistence
  useEffect(() => {
    const path = location.pathname;

    // Skip redirection for /deeplink
    if (path === '/deeplink') return;

    // Check if we are already at a prefixed route or if it matches /:prefix/path
    const hasPrefix = !!getUrlPrefix(path);

    if (!hasPrefix) {
      const savedPodIp = getSavedPodIP();
      if (savedPodIp) {
        // Redirect to the saved prefix
        const newPath = `/${savedPodIp}${path === '/' ? '' : path}`;
        navigate(newPath, { replace: true });
      }
    }
  }, [location.pathname, navigate]);



  useEffect(() => {
    if (userData) {
      if (JSON.stringify(userData) !== JSON.stringify(lastStoredUserData.current)) {
        setUserData(userData);
        lastStoredUserData.current = userData;
      }
    }
  }, [userData, setUserData]);

  const isRedirectPage = location.pathname === '/deeplink';
  if ((authLoading || loading) && !isRedirectPage) {
    return <LoadingScreen />;
  }

  // Show authentication error if auth fails
  if (authError) {
    return (
      <div className="app">
        <div className="error-container">
          <h2>{t('error')}</h2>
          <p>{authError}</p>
          <button onClick={() => window.location.reload()}>
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('App error:', error);
  }

  return (
    <div className="app">
      <Toaster {...TOAST_CONFIG} />
      <TopBar />
      <WebSocketStatus />
      <Routes>
        <Route path="/deeplink" element={<RedirectPage />} />
        <Route path="/:prefix/*" element={<PrefixedRoutes userData={userData} />} />
        <Route path="*" element={<LoadingScreen />} />
      </Routes>
    </div>
  );
};


const App = () => {
  return (
    <LocalizationProvider>
      <AuthProvider>
        <AppProvider>
          <WebSocketProvider>
              <Router>
                <AIAvatarSessionProvider>
                  <AppContent />
                </AIAvatarSessionProvider>
              </Router>
          </WebSocketProvider>
        </AppProvider>
      </AuthProvider>
    </LocalizationProvider>
  );
};

export default App;
